import React from 'react';
import { motion } from 'framer-motion';
import { 
  Target, 
  Users, 
  Sparkles, 
  Code2, 
  Zap, 
  ShieldCheck, 
  Rocket, 
  LineChart 
} from 'lucide-react';

const steps = [
  {
    phase: "Phase 01",
    tag: "Commercial Alignment",
    title: "Discovery & Business Architecture",
    content: "I dig into your underlying business drivers, target unit economics, and operational bottlenecks before touching a design tool or writing code. Every technical choice is mapped directly to a measurable commercial outcome.",
    icon: Target
  },
  {
    phase: "Phase 02",
    tag: "Frictionless UX",
    title: "User Journey & Information Architecture",
    content: "I design intuitive flows centered on real human behavior rather than abstract templates. We structure the information hierarchy and database schemas so users accomplish their primary tasks with zero cognitive friction.",
    icon: Users
  },
  {
    phase: "Phase 03",
    tag: "Risk Elimination",
    title: "Interactive Prototyping & MVP Scoping",
    content: "We create rapid, high-fidelity prototypes and proof-of-concepts to validate usability and product assumptions with real customers and stakeholders before investing heavy engineering capital.",
    icon: Sparkles
  },
  {
    phase: "Phase 04",
    tag: "Production Engineering",
    title: "Modular, Type-Safe Full Stack Build",
    content: "Developing clean, maintainable systems with strict TypeScript typing, responsive modern interfaces, resilient API layers, and scalable database schemas that your internal team can easily own and extend.",
    icon: Code2
  },
  {
    phase: "Phase 05",
    tag: "Operational Leverage",
    title: "Smart Automation & AI Workflow Integration",
    content: "Layering in custom AI agent workflows, automated data ingestion pipelines, and third-party integrations that eliminate repetitive manual labor and dramatically reduce ongoing operational overhead.",
    icon: Zap
  },
  {
    phase: "Phase 06",
    tag: "Enterprise Hardening",
    title: "Security, Speed & Performance Audits",
    content: "Rigorous stress testing, role-based authorization verification, database indexing, and client-side optimization to guarantee sub-second load times and rock-solid stability under peak user traffic.",
    icon: ShieldCheck
  },
  {
    phase: "Phase 07",
    tag: "Zero-Downtime Launch",
    title: "Go-To-Market Deployment & Telemetry",
    content: "Executing flawless cloud deployments with automated CI/CD pipelines, SSL provisioning, payment processing setups, and granular event telemetry to track conversions from moment one.",
    icon: Rocket
  },
  {
    phase: "Phase 08",
    tag: "Sustainable Scale",
    title: "Data-Driven Iteration & Handover",
    content: "Using real production metrics and user session analytics to refine features. Delivering comprehensive documentation, architecture diagrams, and team training so you retain 100% independence.",
    icon: LineChart
  }
];

export const Process: React.FC = () => {
  return (
    <section className="py-20 sm:py-24 md:py-32 bg-black/90 text-white border-t border-white/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        
        <div className="mb-16 sm:mb-20 md:mb-28 text-center md:text-left">
             <motion.span 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-xs sm:text-sm font-mono text-[#d8aa68] uppercase tracking-widest block mb-4"
             >
                My Working Model
             </motion.span>
             <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-3xl sm:text-5xl md:text-6xl font-display font-bold text-white tracking-tight uppercase leading-[1.1]"
             >
                Architecture First. <br className="hidden sm:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white/90 to-[#d8aa68]">
                  Viability Always.
                </span>
             </motion.h2>
             <p className="text-gray-400 text-sm sm:text-base max-w-2xl mt-4 font-light leading-relaxed">
                A transparent, 8-phase execution methodology designed to eliminate technical risk, accelerate time-to-market, and build digital products that produce compounding enterprise value.
             </p>
        </div>

        <div className="relative">
            {/* Vertical Timeline Line - Desktop Center */}
            <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-white/5 hidden md:block -translate-x-1/2 pointer-events-none z-0"></div>
            <motion.div 
                className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#d8aa68] via-[#8B4513] to-transparent hidden md:block origin-top -translate-x-1/2 pointer-events-none z-0"
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={{ once: true, margin: "-20%" }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
            ></motion.div>

            {/* Vertical Timeline Line - Mobile Left */}
            <div className="absolute left-4 sm:left-6 top-0 bottom-0 w-[2px] bg-white/5 md:hidden -translate-x-1/2 pointer-events-none z-0"></div>
            <motion.div 
                className="absolute left-4 sm:left-6 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#d8aa68] via-[#8B4513] to-transparent md:hidden origin-top -translate-x-1/2 pointer-events-none z-0"
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={{ once: true, margin: "-20%" }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
            ></motion.div>

            <div className="flex flex-col gap-10 sm:gap-14 md:gap-20 relative z-10">
                {steps.map((step, i) => (
                    <div 
                        key={step.title}
                        className={`relative flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-0 items-start md:items-center ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                    >
                        {/* Mobile Compact Node */}
                        <div className="absolute left-4 sm:left-6 -translate-x-1/2 top-6 md:hidden z-10 pointer-events-none">
                             <motion.div 
                                initial={{ scale: 0, rotate: -90 }}
                                whileInView={{ scale: 1, rotate: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.4, delay: 0.1, type: "spring" }}
                                className="w-10 h-10 rounded-full bg-[#1a0c06] border border-[#d8aa68]/80 flex items-center justify-center text-[#ffe0a3] shadow-[0_0_10px_rgba(216,170,104,0.3)]"
                             >
                                <step.icon size={16} />
                            </motion.div>
                        </div>

                        {/* Content Side */}
                        <div className={`flex-1 w-full pl-12 sm:pl-16 md:pl-0 text-left relative z-20 ${i % 2 === 0 ? 'md:pr-12 lg:pr-20 md:text-right' : 'md:pl-12 lg:pl-20 md:text-left'}`}>
                            <motion.div 
                                initial={{ opacity: 0, y: 30, scale: 0.96, filter: 'blur(8px)' }}
                                whileInView={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.5, delay: 0.05, ease: "easeOut" }}
                                whileHover={{ scale: 1.01, y: -3 }}
                                className="flex flex-col gap-2.5 sm:gap-3 items-start md:items-stretch bg-[#1a0c06] border border-[#d8aa68]/30 p-5 sm:p-7 rounded-2xl sm:rounded-3xl hover:border-[#d8aa68]/65 transition-all shadow-xl relative overflow-hidden group"
                                style={{
                                    backgroundColor: '#180c06',
                                    backgroundImage: `
                                      radial-gradient(ellipse at 50% 10%, rgba(95, 45, 22, 0.45) 0%, rgba(24, 12, 6, 0.95) 85%, #100603 100%),
                                      url("data:image/svg+xml,%3Csvg width='160' height='160' viewBox='0 0 160 160' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='leather'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0.16  0 0 0 0 0.08  0 0 0 0 0.03  0 0 0 0.45 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23leather)'/%3E%3C/svg%3E")
                                    `,
                                    backgroundBlendMode: 'normal, multiply'
                                }}
                            >
                                {/* Saddle stitch line top & bottom */}
                                <div 
                                  className="absolute top-0 inset-x-0 h-[2px] opacity-40 pointer-events-none"
                                  style={{
                                    backgroundImage: 'linear-gradient(90deg, #d8aa68 0px, #d8aa68 5px, transparent 5px, transparent 10px)',
                                    backgroundSize: '10px 2px'
                                  }}
                                />
                                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-[#d8aa68]/0 via-[#d8aa68]/70 to-[#d8aa68]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                                
                                <div className={`flex items-center gap-2 ${i % 2 === 0 ? 'md:justify-end' : 'md:justify-start'}`}>
                                  <span className="text-[11px] font-mono text-[#ffe0a3] font-bold uppercase tracking-wider bg-[#381b0e]/70 px-2 py-0.5 rounded border border-[#d8aa68]/40 shadow-sm">
                                      {step.phase}
                                  </span>
                                  <span className="text-[10px] font-mono text-[#d8aa68]/90 uppercase tracking-widest">
                                      {step.tag}
                                  </span>
                                </div>

                                <h3 className={`text-lg sm:text-xl md:text-2xl font-bold text-white tracking-tight ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                                    {step.title}
                                </h3>
                                <p className={`text-gray-400 text-xs sm:text-sm leading-relaxed font-light ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                                    {step.content}
                                </p>
                            </motion.div>
                        </div>

                        {/* Desktop Center Compact Node */}
                        <div className="hidden md:flex items-center justify-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none flex-shrink-0">
                            <motion.div 
                                initial={{ scale: 0, rotate: -90 }}
                                whileInView={{ scale: 1, rotate: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.4, delay: 0.1, type: "spring" }}
                                className="w-14 h-14 rounded-full bg-[#1a0c06] border-2 border-[#d8aa68]/70 flex items-center justify-center relative text-[#ffe0a3] shadow-[0_0_16px_rgba(216,170,104,0.35)]"
                            >
                                <step.icon size={20} />
                                <div className="absolute inset-0 bg-[#d8aa68]/15 rounded-full animate-ping opacity-15 pointer-events-none -z-10"></div>
                            </motion.div>
                        </div>

                        {/* Empty Side for Balance (Desktop only) */}
                        <div className="flex-1 w-full hidden md:block"></div>
                    </div>
                ))}
            </div>
        </div>

      </div>
    </section>
  );
};